import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, X, Heart, Check, Download, Share2, Info } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { mediaUrl } from "@/lib/api";

export default function Lightbox({
  items,
  index,
  slug,
  onClose,
  onIndexChange,
  onToggleFavorite,
  favorites = [],
  onToggleSelect,
  selected = [],
  onDownload,
  onShare,
  canDownload = false,
}) {
  const { t, formatDate } = useI18n();
  const [showInfo, setShowInfo] = useState(false);
  const touchStart = useRef(null);
  const item = items[index];

  const go = useCallback(
    (delta) => {
      if (!items.length) return;
      onIndexChange((index + delta + items.length) % items.length);
    },
    [index, items, onIndexChange]
  );

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [go, onClose]);

  if (!item) return null;
  const isFav = favorites.includes(item.id);
  const isSel = selected.includes(item.id);

  const ctrl =
    "grid h-11 w-11 place-items-center border border-[#C69A3B]/60 text-[#F6F0E4] transition-colors duration-300 hover:bg-[#C69A3B] hover:text-[#252525]";

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={item.filename}
      data-testid="lightbox"
      className="fixed inset-0 z-[70] flex flex-col bg-[#161616]/95"
      onTouchStart={(e) => (touchStart.current = e.touches[0].clientX)}
      onTouchEnd={(e) => {
        if (touchStart.current == null) return;
        const dx = e.changedTouches[0].clientX - touchStart.current;
        if (Math.abs(dx) > 50) go(dx < 0 ? 1 : -1);
        touchStart.current = null;
      }}
    >
      <div className="flex items-center justify-between gap-3 border-b border-[#C69A3B]/25 px-4 py-3">
        <p className="label-mono">
          {index + 1} / {items.length}
        </p>
        <div className="flex items-center gap-2">
          {onToggleFavorite && (
            <button
              type="button"
              className={ctrl}
              data-testid="lightbox-favorite-button"
              aria-label={t("gallery.favorite")}
              onClick={() => onToggleFavorite(item)}
            >
              <Heart size={17} fill={isFav ? "#C69A3B" : "none"} />
            </button>
          )}
          {onToggleSelect && (
            <button
              type="button"
              className={`${ctrl} ${isSel ? "bg-[#C69A3B] text-[#252525]" : ""}`}
              data-testid="lightbox-select-button"
              aria-label={t("gallery.select")}
              onClick={() => onToggleSelect(item)}
            >
              <Check size={17} />
            </button>
          )}
          {canDownload && (
            <button
              type="button"
              className={ctrl}
              data-testid="lightbox-download-button"
              aria-label={t("btn.downloadPhoto")}
              onClick={() => onDownload(item)}
            >
              <Download size={17} />
            </button>
          )}
          {onShare && (
            <button
              type="button"
              className={ctrl}
              data-testid="lightbox-share-button"
              aria-label={t("btn.share")}
              onClick={() => onShare(item)}
            >
              <Share2 size={17} />
            </button>
          )}
          <button
            type="button"
            className={ctrl}
            data-testid="lightbox-info-button"
            aria-label={t("gallery.info")}
            onClick={() => setShowInfo((v) => !v)}
          >
            <Info size={17} />
          </button>
          <button
            type="button"
            className={ctrl}
            data-testid="lightbox-close-button"
            aria-label={t("nav.close")}
            onClick={onClose}
          >
            <X size={17} />
          </button>
        </div>
      </div>

      <div className="relative flex flex-1 items-center justify-center overflow-hidden p-3 sm:p-8">
        <button
          type="button"
          onClick={() => go(-1)}
          data-testid="lightbox-prev-button"
          aria-label={t("btn.prev")}
          className="absolute left-2 z-10 grid h-12 w-12 place-items-center border border-[#C69A3B]/50 bg-[#161616]/60 text-[#F6F0E4] transition-colors duration-300 hover:bg-[#C69A3B] hover:text-[#252525]"
        >
          <ChevronLeft size={22} />
        </button>
        {item.kind === "video" ? (
          item.provider ? (
            <iframe
              data-testid="lightbox-video-embed"
              src={`${item.video_url}?rel=0`}
              title={item.title_es || item.title_en || item.filename}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; picture-in-picture; fullscreen"
              allowFullScreen
              className="aspect-video h-auto w-full max-w-5xl border-0"
            />
          ) : (
            <video
              data-testid="lightbox-video"
              src={item.video_url || mediaUrl(item, "orig", slug)}
              poster={item.external_url || mediaUrl(item, "web", slug)}
              controls
              playsInline
              className="max-h-full max-w-full"
            />
          )
        ) : (
          <img
            data-testid="lightbox-image"
            src={item.external_url || mediaUrl(item, "web", slug)}
            alt={item.caption_es || item.filename}
            className="max-h-full max-w-full object-contain"
          />
        )}
        <button
          type="button"
          onClick={() => go(1)}
          data-testid="lightbox-next-button"
          aria-label={t("btn.next")}
          className="absolute right-2 z-10 grid h-12 w-12 place-items-center border border-[#C69A3B]/50 bg-[#161616]/60 text-[#F6F0E4] transition-colors duration-300 hover:bg-[#C69A3B] hover:text-[#252525]"
        >
          <ChevronRight size={22} />
        </button>
      </div>

      {showInfo && (
        <div
          data-testid="lightbox-info-panel"
          className="border-t border-[#C69A3B]/25 bg-[#1d1d1d] px-4 py-4 text-xs text-[#E8D8B6] sm:px-8"
        >
          <div className="grid gap-2 sm:grid-cols-4">
            <p>
              <span className="label-mono block">{t("gallery.filename")}</span> {item.filename}
            </p>
            <p>
              <span className="label-mono block">{t("gallery.photographer")}</span> {item.photographer || "HR Creations"}
            </p>
            <p>
              <span className="label-mono block">{t("gallery.date")}</span> {formatDate(item.capture_date)}
            </p>
            {item.kind === "video" && item.duration && (
              <p>
                <span className="label-mono block">{t("gallery.duration")}</span>
                {Math.floor(item.duration / 60)}:{String(item.duration % 60).padStart(2, "0")}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
